import type { CanonicalPricing, ShopifyOfferDto, StoreDto, UnifiedItem } from '../../offer.types';
import type { CheckoutPreviewResponseDto } from '../../services/checkoutConfig/checkoutConfig.types';

/**
 * Produto fake fixo usado pela prévia ao vivo do checkout (admin). Nunca depende de uma
 * oferta real da loja — só existe pra dar contexto visual ao layout enquanto o lojista
 * edita as abas do editor de checkout.
 */
const FAKE_PRICE = 199.9;

function buildFakePricing(): CanonicalPricing {
  return {
    productsSubtotal: FAKE_PRICE,
    shipping: 0,
    progressiveDiscount: 0,
    couponDiscount: 0,
    paymentMethodDiscount: 0,
    totalDiscounts: 0,
    orderBumpTotal: 0,
    subtotalWithDiscount: FAKE_PRICE,
    total: FAKE_PRICE,
    paymentMethod: 'card',
    byMethod: {
      card: {
        total: FAKE_PRICE,
        paymentMethodDiscount: 0,
        installments: {
          installments: 3,
          installment_amount: FAKE_PRICE / 3,
          cash_price: FAKE_PRICE,
          total_with_interest: FAKE_PRICE,
          has_interest: false,
          available_installments: [
            { installments: 1, amount: FAKE_PRICE, interest_rate: 0, total_amount: FAKE_PRICE, has_interest: false },
            { installments: 3, amount: FAKE_PRICE / 3, interest_rate: 0, total_amount: FAKE_PRICE, has_interest: false },
          ],
        },
      },
      pix: { total: FAKE_PRICE, paymentMethodDiscount: 0 },
      billet: { total: FAKE_PRICE, paymentMethodDiscount: 0 },
    },
  };
}

function buildFakeItems(): UnifiedItem[] {
  return [
    {
      variant_id: 1,
      name: 'Produto de demonstração',
      quantity: 1,
      price: FAKE_PRICE,
      source: 'offer',
      imageUrl: null,
    },
  ];
}

/**
 * Monta um `ShopifyOfferDto` fake mínimo — só o suficiente pra `CheckoutPage` e seus
 * hooks/componentes renderizarem sem depender de uma oferta real. `store` fica com os
 * campos básicos (nome do checkout, id) porque a aparência real (cores, logo, layout)
 * vem do `checkoutConfigOverride`/`paymentBadgesOverride`/`storeContactOverride` — não
 * de `store.*` — no modo preview. `checkoutTheme` recebe a prévia real buscada
 * (`GET /store-checkouts/preview/:nanoId`) só pra satisfazer o tipo; os componentes
 * sempre leem os overrides, nunca este campo.
 */
export function buildFakePreviewOffer(
  checkoutName: string,
  storeNanoId: string,
  checkoutTheme: CheckoutPreviewResponseDto,
): ShopifyOfferDto {
  const store: StoreDto = {
    nanoId: storeNanoId,
    name: checkoutName || 'Prévia do checkout',
    email: 'previa@teamdrop.com.br',
    active: true,
    cpfOnIdentification: true,
    design: {
      id: 0,
      name: 'default',
      kind: 'default',
    },
  };

  return {
    name: 'Prévia',
    uuid: 'preview',
    store,
    price: FAKE_PRICE,
    metadata: { line_items: [] },
    offer_image: [],
    installments: 3,
    shipping_type: 0,
    shipping_price: 0,
    student_pays_interest: false,
    id: 0,
    hash: 'preview',
    nanoId: 'preview',
    cart: {
      name: 'Prévia',
      uuid: 'preview',
      price: String(FAKE_PRICE),
      metadata: { line_items: [] },
      offer_image: [],
      installments: 3,
      shipping_type: 0,
      shipping_price: 0,
      student_pays_interest: false,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
    storeId: storeNanoId,
    quantity: 1,
    original_price: FAKE_PRICE,
    prices: { billet: FAKE_PRICE, card: FAKE_PRICE, pix: FAKE_PRICE },
    hasActiveCoupon: false,
    pricing: buildFakePricing(),
    offer: { name: 'Prévia', alternative_name: null },
    discounts: { billet: 0, card: 0, pix: 0 },
    require_address: true,
    payment: {
      type: 'default',
      methods: ['credit_card', 'pix', 'boleto'],
      installments: 3,
      plans: [],
      student_pays_interest: false,
      creditCardPrimaryGateway: undefined,
      creditCardGatewayRequires3DS: false,
    },
    checkout: {
      hex_color: '#3B82F6',
      sidebar_picture: '',
      header_picture: '',
      header_picture_mobile: '',
      favicon: null,
    },
    product: {
      id: 0,
      name: 'Produto de demonstração',
      description: '',
      type: 'digital',
      content_delivery: '',
      cover: '',
      sales_page_url: '',
      support_email: '',
      support_whatsapp: '',
      logo: '',
    },
    pixels: {},
    full_name: null,
    verified_id: false,
    terms: false,
    url_terms: null,
    upsell: false,
    order_bumps: [],
    gifts: [],
    additional_costs: { order_bump_total: 0, total: 0 },
    items: buildFakeItems(),
    checkoutTheme,
  };
}
